import {
  Ancestry,
  Contact,
  EnforcementSchedule,
  Entity,
  PermitTransaction,
  Pricing,
  Property,
  PropertyGroup,
  RateLimit,
  Status,
  Tenant,
  TimePeriod,
  UnitOfTime,
  UnixTimestampMS,
  Vehicle,
} from 'types';

export enum InviteStatus {
  Sent = 'sent',
  Pending = 'pending',
  Failed = 'failed',
  RenewalSent = 'renewal_sent',
  None = 'none',
}

export enum PermitStatus {
  Active = 'active',
  Disabled = 'disabled',
  PendingRegistration = 'pending_registration',
  PendingPayment = 'pending_payment',
  PendingApproval = 'pending_approval',
  Expired = 'expired',
  ApprovalExpired = 'approval_expired',
}

// this is the list of fields that are displayed in the permit wizard
export type PermitField =
  // | 'permit.permitNumber'
  | 'permit.permitStatus'
  | 'permit.startTime'
  | 'permit.endTime'
  | 'permit.vehicle.make'
  | 'permit.vehicle.model'
  | 'permit.vehicle.year'
  | 'permit.vehicle.color'
  | 'permit.vehicle.vin'
  | 'permit.vehicle.plate.number'
  | 'permit.vehicle.plate.state'
  | 'permit.contact.name'
  | 'permit.contact.location.street'
  | 'permit.contact.location.city'
  | 'permit.contact.location.stateCode'
  | 'permit.contact.location.postalCode'
  | 'permit.contact.location.unit'
  | 'permit.contact.primaryEmail'
  | 'permit.contact.primaryPhone';

export interface Permit {
  id: number;
  created?: Date | string;
  updated?: Date | string;
  inviteStatus: InviteStatus;
  permitStatus: PermitStatus;
  permitNumber?: string;
  startTime: UnixTimestampMS;
  endTime?: UnixTimestampMS;
  vehicle: Vehicle;
  property?: Partial<Property>;
  propertyGroup?: Partial<PropertyGroup>;
  contact: Contact;
  permitType: PermitType;
  tenant: Partial<Tenant>;
  data: PermitData;
  transactions: PermitTransaction[];
}
export interface PermitData {
  cancelReason?: string;
  renewalDate?: UnixTimestampMS;
  spot?: string;
}
export interface PermitInviteBody {
  name: string;
  email?: string;
  phone?: string;
  entityName: string; // could be property or property group
  selfService: boolean;
  permitTypeID: number;
}

export enum PermitNotificationType {
  RenewalNudge = 'renewalNudge',
  Expiration = 'expiration',
  Revoked = 'revoked',
}

export interface PermitNotification {
  type: PermitNotificationType;
  unitOfTime?: UnitOfTime | undefined;
  amountOfTime?: number | undefined;
}
export interface FieldConfiguration {
  enabled: boolean;
  required: boolean;
}

export interface PermitFieldConfiguration {
  vin?: FieldConfiguration;
  color?: FieldConfiguration;
  makeModel?: FieldConfiguration;
  driverLicense?: FieldConfiguration;
}

export interface PermitTypeData {
  selfService: boolean;
  needsApproval: boolean;
  termsAndConditions?: string;
  notifications?: PermitNotification[] | undefined;
  defaultTerm?: TimePeriod;
  cooldown?: PermitCooldown;
  quantityOfSpots?: number;
  guestPermitData?: GuestPermitData;
  fieldConfig?: PermitFieldConfiguration;
}

export interface GuestPermitData {
  enabled: boolean;
  term: TimePeriod;
  rate: number;
  unitOfTime: UnitOfTime;
}

export interface PermitType extends Entity {
  description?: string;
  status: Status;
  tenantID: number;
  permitPricingID: number;
  permitPricing?: Pricing;
  entityTypeID?: number;
  entityID?: number;
  startDate: Date | UnixTimestampMS;
  endDate?: Date | UnixTimestampMS;
  created?: Date | UnixTimestampMS;
  updated?: Date | UnixTimestampMS;
  purchaseLeadTime?: number; // hours
  data: PermitTypeData;
  ancestry?: Ancestry;
}

export interface PermitCooldown extends RateLimit {
  enabled: boolean;
  target: 'vehicle' | 'person';
}

export interface EntityData {
  enforcementSchedule: EnforcementSchedule; // default always
  overrideOtherPermits: boolean;
}
